import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FiLayers, FiEdit3, FiClock, FiSend, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import { toast } from "react-toastify";
import CampaignStatsCard from "./CampaignStatsCard";
import CampaignTable from "./CampaignTable";
import CreateCampaignDrawer from "./CreateCampaignDrawer";
import CampaignDetailDrawer from "./CampaignDetailDrawer";
import { getCampaigns, getCampaignDetail, cancelCampaign } from "../../services/campaignService";

const FILTER_FN = {
  draft: (c) => c.status === "DRAFT",
  scheduled: (c) => ["SCHEDULED", "QUEUED"].includes(c.status),
  running: (c) => c.status === "RUNNING",
  completed: (c) => ["COMPLETED", "PARTIAL_SUCCESS"].includes(c.status),
  failed: (c) => c.status === "FAILED",
};

const CampaignsDashboard = () => {
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [activeFilter, setActiveFilter] = useState(null);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editingCampaignData, setEditingCampaignData] = useState(null);
  const [viewingCampaignId, setViewingCampaignId] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      setTableLoading(true);
      const res = await getCampaigns();
      const rows = Array.isArray(res.data) ? res.data : [];
      setAllCampaigns(
        rows.map((item) => ({
          id: item.id,
          name: item.name,
          channel: item.channel,
          audienceType: item.audience_type,
          recipientCount: item.recipient_count ?? 0,
          sentCount: item.sent_count ?? 0,
          failedCount: item.failed_count ?? 0,
          status: item.status,
          sendMode: item.send_mode,
          scheduledAt: item.scheduled_at,
          createdAt: item.created_at,
        }))
      );
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const totalCount = allCampaigns.length;
  const draftCount = useMemo(() => allCampaigns.filter(FILTER_FN.draft).length, [allCampaigns]);
  const scheduledCount = useMemo(() => allCampaigns.filter(FILTER_FN.scheduled).length, [allCampaigns]);
  const runningCount = useMemo(() => allCampaigns.filter(FILTER_FN.running).length, [allCampaigns]);
  const completedCount = useMemo(() => allCampaigns.filter(FILTER_FN.completed).length, [allCampaigns]);
  const failedCount = useMemo(() => allCampaigns.filter(FILTER_FN.failed).length, [allCampaigns]);

  const filteredCampaigns = useMemo(() => {
    const fn = FILTER_FN[activeFilter];
    return fn ? allCampaigns.filter(fn) : allCampaigns;
  }, [allCampaigns, activeFilter]);

  const sortedCampaigns = useMemo(() => {
    if (!sortBy) return filteredCampaigns;
    return [...filteredCampaigns].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const cmp = typeof aVal === "number" ? aVal - bVal : String(aVal || "").localeCompare(String(bVal || ""));
      return sortOrder === "desc" ? -cmp : cmp;
    });
  }, [filteredCampaigns, sortBy, sortOrder]);

  const displayedCampaigns = useMemo(() => {
    const start = (page - 1) * limit;
    return sortedCampaigns.slice(start, start + limit);
  }, [sortedCampaigns, page, limit]);

  const handleSort = (col, order) => {
    setSortBy(col);
    setSortOrder(order);
    setPage(1);
  };

  const handleTileFilter = (filter) => {
    setActiveFilter((prev) => (prev === filter ? null : filter));
    setPage(1);
  };

  const handleCreateSuccess = () => fetchCampaigns();

  const handleEditClick = async (row) => {
    try {
      const res = await getCampaignDetail(row.id);
      if (res.success) setEditingCampaignData(res.data);
      else toast.error(res.message || "Failed to load campaign details");
    } catch {
      toast.error("Failed to load campaign details");
    }
  };

  const handleCancel = async (row) => {
    if (!window.confirm(`Cancel "${row.name}"? This cannot be undone.`)) return;
    try {
      const res = await cancelCampaign(row.id);
      if (res.success) {
        toast.success("Campaign cancelled");
        fetchCampaigns();
      } else {
        toast.error(res.message || "Failed to cancel campaign");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to cancel campaign");
    }
  };

  return (
    <div style={{ padding: "24px", background: "#F3F4F6", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#111827" }}>Campaigns</h1>
        <button
          onClick={() => setShowCreateDrawer(true)}
          style={{
            background: "#C0335A",
            color: "#fff",
            border: "none",
            borderRadius: "9999px",
            padding: "12px 24px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#a52b4e")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#C0335A")}
        >
          + Create Campaign
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <CampaignStatsCard icon={<FiLayers />} label="Total" value={totalCount} loading={tableLoading} onClick={() => handleTileFilter(null)} isActive={activeFilter === null} />
        <CampaignStatsCard icon={<FiEdit3 />} label="Draft" value={draftCount} loading={tableLoading} onClick={() => handleTileFilter("draft")} isActive={activeFilter === "draft"} />
        <CampaignStatsCard icon={<FiClock />} label="Scheduled" value={scheduledCount} loading={tableLoading} onClick={() => handleTileFilter("scheduled")} isActive={activeFilter === "scheduled"} />
        <CampaignStatsCard icon={<FiSend />} label="Running" value={runningCount} loading={tableLoading} onClick={() => handleTileFilter("running")} isActive={activeFilter === "running"} />
        <CampaignStatsCard icon={<FiCheckCircle />} label="Completed" value={completedCount} loading={tableLoading} onClick={() => handleTileFilter("completed")} isActive={activeFilter === "completed"} />
        <CampaignStatsCard icon={<FiAlertTriangle />} label="Failed" value={failedCount} loading={tableLoading} onClick={() => handleTileFilter("failed")} isActive={activeFilter === "failed"} />
      </div>

      <div style={{ background: "#fff", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden", paddingBottom: "16px" }}>
        <CampaignTable
          data={displayedCampaigns}
          loading={tableLoading}
          page={page}
          limit={limit}
          total={filteredCampaigns.length}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onPageChange={setPage}
          onLimitChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
          onView={(row) => setViewingCampaignId(row.id)}
          onEdit={handleEditClick}
          onCancel={handleCancel}
        />
      </div>

      {showCreateDrawer && (
        <CreateCampaignDrawer onClose={() => setShowCreateDrawer(false)} onSuccess={handleCreateSuccess} />
      )}

      {editingCampaignData && (
        <CreateCampaignDrawer
          onClose={() => setEditingCampaignData(null)}
          onSuccess={() => { setEditingCampaignData(null); fetchCampaigns(); }}
          isEditMode={true}
          initialData={editingCampaignData}
        />
      )}

      {viewingCampaignId && (
        <CampaignDetailDrawer campaignId={viewingCampaignId} onClose={() => setViewingCampaignId(null)} />
      )}
    </div>
  );
};

export default CampaignsDashboard;
